import { useEffect, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react';

import { useQuery } from '@apollo/client/react';
import { Command } from 'cmdk';
import {
  ArrowRight,
  Building2,
  Clock,
  CornerDownLeft,
  CreditCard,
  FileText,
  LogIn,
  Search,
  Settings as SettingsIcon,
  Upload,
  User,
  Users2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { useTenantFeatures } from '../../hooks/useTenantFeatures';
import { TenantFeature } from '../../types/tenant.types';
import { formatCurrency } from '../../utils/pricingCalculator';
import { Button } from '../Button/Button';
import type {
  GlobalSearchQuery,
  GlobalSearchQueryVariables,
} from '../RecordSearch/globalSearch.query.graphql';
import GLOBAL_SEARCH from '../RecordSearch/globalSearch.query.graphql';
import { Highlight } from '../RecordSearch/Highlight';
import { MatchedOn } from '../RecordSearch/MatchedOn';

import { useCommandPalette } from './CommandPaletteContext';
import { NAVIGATE_TARGETS } from './navigateTargets';
import { readRecentItems, rememberRecentItem, type RecentItem } from './recentItems';
import { trackCmdkSelect, type CmdkSelectEvent } from './telemetry';

import styles from './CommandPalette.module.css';

/** Short: the server answers each kind from an index, so waiting longer buys nothing. */
const DEBOUNCE_MS = 120;
const HITS_PER_KIND = 5;

type RawHit = GlobalSearchQuery['globalSearch']['groups'][number]['hits'][number];
/** The API's GlobalSearchType values, as plain strings. */
type Kind = 'CLIENT' | 'CONTACT' | 'INVOICE' | 'SUBSCRIPTION';
type Hit = Omit<RawHit, 'type'> & { type: Kind };
interface Group {
  type: Kind;
  hits: Hit[];
  totalCount: number;
  totalCapped: boolean;
}
type Scope = 'ALL' | Kind;

interface ActionItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}

const ACTION_ITEMS: ActionItem[] = [
  // No first-class "switch tenant" trigger exists today — routing to /account puts the
  // user next to the tenant switcher chip. TODO(phase-5): imperative open-switcher API.
  { id: 'action-switch-tenant', label: 'Switch tenant…', path: '/account', icon: Users2 },
  // Impersonation requires a linked client, which is picked from the Clients list.
  { id: 'action-impersonate', label: 'Start impersonation…', path: '/clients', icon: LogIn },
  { id: 'action-export-users', label: 'Export users…', path: '/users/export', icon: Upload },
  { id: 'action-settings', label: 'Open settings', path: '/settings', icon: SettingsIcon },
];

const KIND_LABEL: Record<Kind, string> = {
  CLIENT: 'Clients & companies',
  CONTACT: 'Contacts',
  INVOICE: 'Invoices',
  SUBSCRIPTION: 'Subscriptions',
};

const KIND_ICON: Record<Kind, React.ComponentType<{ size?: number; className?: string }>> = {
  CLIENT: Building2,
  CONTACT: User,
  INVOICE: FileText,
  SUBSCRIPTION: CreditCard,
};

const KIND_TELEMETRY: Record<Kind, CmdkSelectEvent['type']> = {
  CLIENT: 'clients',
  CONTACT: 'contact',
  INVOICE: 'invoice',
  SUBSCRIPTION: 'subscription',
};

const INTENT_LABEL: Record<string, string> = {
  email: 'Looks like an email: contacts first.',
  invoiceNumber: 'Looks like an invoice number: exact match first.',
  phone: 'Looks like a phone number: contacts first.',
  taxId: 'Looks like a tax ID.',
};

function hitPath(hit: Hit): string {
  switch (hit.type) {
    case 'CLIENT':
      return `/clients/${hit.id}`;
    case 'CONTACT':
      // Contacts have no page of their own; open the list filtered to this person.
      return `/contacts?q=${encodeURIComponent(hit.subtitle || hit.title)}`;
    case 'INVOICE':
      return `/invoices/${hit.id}`;
    case 'SUBSCRIPTION':
      return hit.organizationId ? `/clients/${hit.organizationId}/subscriptions` : '/subscriptions';
  }
}

function seeAllPath(kind: Kind, query: string, hasCrm: boolean): string | null {
  const q = encodeURIComponent(query);
  switch (kind) {
    case 'CLIENT':
      return hasCrm ? `/companies?q=${q}` : `/clients?q=${q}`;
    case 'CONTACT':
      return `/contacts?q=${q}`;
    case 'INVOICE':
      return `/finance?tab=all&q=${q}`;
    case 'SUBSCRIPTION':
      return null;
  }
}

function statusPillClass(status: string): string {
  switch (status) {
    case 'PAID':
    case 'ACTIVE':
      return styles.StatusPillPaid;
    case 'OVERDUE':
    case 'FAILED':
      return styles.StatusPillOverdue;
    case 'PENDING':
      return styles.StatusPillPending;
    default:
      return styles.StatusPillNeutral;
  }
}

/** Only statuses worth a pill: a client's stage is shown as context instead. */
function pillFor(hit: Hit): string | null {
  if (!hit.status) return null;
  if (hit.type === 'INVOICE' || hit.type === 'SUBSCRIPTION') return hit.status;
  if (hit.type === 'CLIENT' && hit.status !== 'CLIENT') return hit.status;
  return null;
}

export function CommandPalette(): JSX.Element {
  const { open, setOpen, initialQuery } = useCommandPalette();
  const navigate = useNavigate();
  const { hasFeature } = useTenantFeatures();
  const hasCrm = hasFeature(TenantFeature.CRM);

  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<Scope>('ALL');
  const [recent, setRecent] = useState<RecentItem[]>([]);
  const [debouncedQuery] = useDebounce(query, DEBOUNCE_MS);
  const trimmed = debouncedQuery.trim();
  const hasQuery = trimmed.length > 0;

  // Fresh input and recents every time the palette opens.
  useEffect(() => {
    if (open) {
      setRecent(readRecentItems());
      if (initialQuery) setQuery(initialQuery);
    } else {
      setQuery('');
      setScope('ALL');
    }
  }, [open, initialQuery]);

  // Track Cmd/Ctrl state so `onSelect` can decide new-tab vs navigate. cmdk's
  // onSelect only receives the item value, not the triggering event.
  const modifierHeldRef = useRef(false);
  useEffect(() => {
    const down = (e: globalThis.KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) modifierHeldRef.current = true;
    };
    const up = (e: globalThis.KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) modifierHeldRef.current = false;
    };
    document.addEventListener('keydown', down);
    document.addEventListener('keyup', up);
    return () => {
      document.removeEventListener('keydown', down);
      document.removeEventListener('keyup', up);
    };
  }, []);

  // One request for every kind. `previousData` keeps the last results on screen
  // while the next keystroke's answer is in flight, so the list never blanks.
  const { data, previousData, loading } = useQuery<GlobalSearchQuery, GlobalSearchQueryVariables>(
    GLOBAL_SEARCH,
    {
      variables: { input: { query: trimmed, limit: HITS_PER_KIND } },
      skip: !open || !hasQuery,
      fetchPolicy: 'cache-first',
    },
  );
  const result = (data ?? previousData)?.globalSearch;
  const groups = (hasQuery ? (result?.groups ?? []) : []) as unknown as Group[];
  const visibleGroups = scope === 'ALL' ? groups : groups.filter((g) => g.type === scope);

  // Static groups filtered client-side. cmdk's own filter is disabled (shouldFilter=false)
  // so we can mix instant static hits with server-filtered async results in the same list.
  const normalized = query.trim().toLowerCase();
  const matches = (text: string) => !normalized || text.toLowerCase().includes(normalized);

  const visibleNavigate = useMemo(
    () =>
      scope !== 'ALL'
        ? []
        : NAVIGATE_TARGETS.filter((t) => {
            // Feature-gated pages redirect to /dashboard when the tenant lacks the
            // feature, so don't offer them here either.
            if (t.feature && !hasFeature(t.feature)) return false;
            if (t.anyFeature && !t.anyFeature.some((flag) => hasFeature(flag))) return false;
            return (
              matches(t.label) ||
              matches(t.section) ||
              matches(t.path) ||
              (t.keywords ?? []).some((keyword) => matches(keyword))
            );
          }).slice(0, hasQuery ? 4 : 8),
    [normalized, hasFeature, scope, hasQuery],
  );

  const visibleActions = useMemo(
    () => (scope !== 'ALL' ? [] : ACTION_ITEMS.filter((a) => matches(a.label))),
    [normalized, scope],
  );

  const scopes: Scope[] = ['ALL', ...groups.map((g) => g.type)];
  const cycleScope = (direction: 1 | -1) => {
    const at = Math.max(scopes.indexOf(scope), 0);
    setScope(scopes[(at + direction + scopes.length) % scopes.length]);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Tab narrows to one kind of record, Shift+Tab goes back; Backspace on an
    // empty input leaves the scope, as in most pickers.
    if (event.key === 'Tab' && groups.length > 0) {
      event.preventDefault();
      cycleScope(event.shiftKey ? -1 : 1);
    } else if (event.key === 'Backspace' && query === '' && scope !== 'ALL') {
      setScope('ALL');
    }
  };

  const go = (path: string, type: CmdkSelectEvent['type']) => {
    const newTab = modifierHeldRef.current;
    trackCmdkSelect({ type, queryLength: query.length, openedInNewTab: newTab });
    if (newTab) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(path);
    setOpen(false);
  };

  const openHit = (hit: Hit) => {
    const path = hitPath(hit);
    rememberRecentItem({
      kind: hit.type,
      id: hit.id,
      title: hit.title,
      subtitle: hit.organizationName ?? hit.subtitle,
      path,
    });
    go(path, KIND_TELEMETRY[hit.type]);
  };

  const showRecent = !hasQuery && query.trim() === '' && recent.length > 0;
  const searching = hasQuery && loading && !result;
  const nothingVisible =
    !showRecent &&
    visibleNavigate.length === 0 &&
    visibleActions.length === 0 &&
    visibleGroups.length === 0 &&
    !searching;

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command palette"
      shouldFilter={false}
      overlayClassName={styles.Overlay}
      contentClassName={styles.Content}
    >
      <div className={styles.Root}>
        <div className={styles.InputRow}>
          <Search className={styles.InputIcon} aria-hidden="true" />
          {scope !== 'ALL' && <span className={styles.ScopeTag}>{KIND_LABEL[scope]}</span>}
          <Command.Input
            autoFocus
            placeholder="Search everything: a name, email, invoice number, tax ID…"
            value={query}
            onValueChange={setQuery}
            onKeyDown={onInputKeyDown}
            className={styles.Input}
          />
          {hasQuery && loading && <span className={styles.Busy}>Searching…</span>}
          <kbd className={styles.EscHint}>Esc</kbd>
        </div>

        {groups.length > 0 && (
          <div className={styles.Scopes} role="toolbar" aria-label="Search in">
            {scopes.map((s) => {
              const group = groups.find((g) => g.type === s);
              const count =
                s === 'ALL'
                  ? groups.reduce((n, g) => n + g.totalCount, 0)
                  : (group?.totalCount ?? 0);
              const capped = s === 'ALL' ? groups.some((g) => g.totalCapped) : group?.totalCapped;
              return (
                <Button
                  key={s}
                  type="button"
                  size="xs"
                  variant={scope === s ? 'secondary' : 'ghost'}
                  aria-pressed={scope === s}
                  className={styles.ScopeChip}
                  onClick={() => setScope(s)}
                >
                  {s === 'ALL' ? 'All' : KIND_LABEL[s]}
                  <span className={styles.ScopeCount}>
                    {count}
                    {capped ? '+' : ''}
                  </span>
                </Button>
              );
            })}
          </div>
        )}

        <Command.List className={styles.List}>
          {nothingVisible && (
            <Command.Empty className={styles.Empty}>
              {hasQuery
                ? `No results for "${query.trim()}". Try part of a name, an email, or an invoice number.`
                : 'Search clients, contacts, invoices and subscriptions by any field.'}
            </Command.Empty>
          )}

          {hasQuery && scope === 'ALL' && result?.intent && INTENT_LABEL[result.intent] && (
            <div className={styles.Intent}>{INTENT_LABEL[result.intent]}</div>
          )}

          {searching && <div className={styles.LoadingRow}>Searching…</div>}

          {showRecent && (
            <Command.Group heading="Recent" className={styles.Group}>
              {recent.map((item) => {
                const Icon = KIND_ICON[item.kind] ?? Clock;
                return (
                  <Command.Item
                    key={`recent:${item.kind}:${item.id}`}
                    value={`recent:${item.kind}:${item.id}`}
                    onSelect={() => go(item.path, 'recent')}
                    className={styles.Item}
                  >
                    <Icon size={16} className={styles.ItemIcon} />
                    <span className={styles.ItemPrimary}>{item.title}</span>
                    {item.subtitle && <span className={styles.ItemMeta}>{item.subtitle}</span>}
                  </Command.Item>
                );
              })}
            </Command.Group>
          )}

          {visibleGroups.map((group) => {
            const Icon = KIND_ICON[group.type];
            const seeAll = seeAllPath(group.type, trimmed, hasCrm);
            const more = group.totalCapped || group.totalCount > group.hits.length;
            return (
              <Command.Group
                key={group.type}
                heading={KIND_LABEL[group.type]}
                className={styles.Group}
              >
                {group.hits.map((hit) => {
                  const pill = pillFor(hit);
                  // A client shows its domain; a subscription is titled by its client, so
                  // it shows its plan; everything else shows the client it belongs to.
                  const context =
                    hit.type === 'CLIENT' || hit.type === 'SUBSCRIPTION'
                      ? hit.subtitle
                      : hit.organizationName;
                  return (
                    <Command.Item
                      key={`${hit.type}:${hit.id}`}
                      value={`${hit.type}:${hit.id}`}
                      onSelect={() => openHit(hit)}
                      className={styles.HitItem}
                    >
                      <Icon size={16} className={styles.ItemIcon} />
                      <span className={styles.HitText}>
                        <span className={styles.ItemPrimary}>
                          <span className={styles.HitTitle}>
                            <Highlight text={hit.title} query={trimmed} />
                          </span>
                          {context && <span className={styles.HitContext}>{context}</span>}
                        </span>
                        <MatchedOn match={hit.match} query={trimmed} hideValueWhen={hit.title} />
                      </span>
                      {hit.amount != null && hit.currency && (
                        <span className={styles.ItemMeta}>
                          {formatCurrency(hit.amount, hit.currency)}
                        </span>
                      )}
                      {pill && (
                        <span
                          className={`${styles.StatusPill} ${statusPillClass(pill)}`}
                          aria-label={`Status: ${pill.toLowerCase()}`}
                        >
                          {pill.toLowerCase()}
                        </span>
                      )}
                    </Command.Item>
                  );
                })}
                {seeAll && more && (
                  <Command.Item
                    value={`see-all:${group.type}`}
                    onSelect={() => go(seeAll, 'see-all')}
                    className={styles.SeeAll}
                  >
                    See all {group.totalCount}
                    {group.totalCapped ? '+' : ''} in {KIND_LABEL[group.type]}
                    <ArrowRight size={14} />
                  </Command.Item>
                )}
              </Command.Group>
            );
          })}

          {visibleNavigate.length > 0 && (
            <Command.Group heading="Go to" className={styles.Group}>
              {visibleNavigate.map((target) => (
                <Command.Item
                  key={`nav:${target.path}`}
                  value={`nav:${target.path}:${target.section}:${target.label}`}
                  onSelect={() => go(target.path, 'navigate')}
                  className={styles.Item}
                >
                  <ArrowRight size={16} className={styles.ItemIcon} />
                  <span className={styles.ItemPrimary}>
                    <span className={styles.ItemSection}>{target.section}</span>
                    <span className={styles.ItemSeparator}>→</span>
                    <span>{target.label}</span>
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {visibleActions.length > 0 && (
            <Command.Group heading="Actions" className={styles.Group}>
              {visibleActions.map((a) => {
                const ActionIcon = a.icon;
                return (
                  <Command.Item
                    key={a.id}
                    value={`act:${a.id}`}
                    onSelect={() => go(a.path, 'action')}
                    className={styles.Item}
                  >
                    <ActionIcon size={16} className={styles.ItemIcon} />
                    <span className={styles.ItemPrimary}>{a.label}</span>
                  </Command.Item>
                );
              })}
            </Command.Group>
          )}
        </Command.List>

        <footer className={styles.Footer}>
          <span className={styles.FooterHint}>
            <CornerDownLeft size={12} /> open
          </span>
          <span className={styles.FooterHint}>
            <kbd className={styles.FooterKbd}>⌘</kbd>
            <CornerDownLeft size={12} /> new tab
          </span>
          <span className={styles.FooterHint}>
            <kbd className={styles.FooterKbd}>Tab</kbd> narrow
          </span>
          <span className={styles.FooterHint}>email: vat: inv: phone: search one field</span>
        </footer>
      </div>
    </Command.Dialog>
  );
}

export default CommandPalette;
